import React, {Component} from "react";
import './DeliveryGroupsMap.scss';
import ChangeGroupDialog from "../ChangeGroupDialog/ChangeGroupDialog";
import _ from "lodash";
import createClone from "rfdc";
import uuid from "uuid";
import DeliveryMapContainer from "../../../Redux/containers/DeliveryMapContainer";
import {shallowEqualObjects} from "shallow-equal";
import {eventsSubject} from "../../../Events/events";
import {filter} from "rxjs/operators";
import {FocusMessengerActiveGroup} from "../../../Events/topics";
import SentRoutesContainer from "../../../Redux/containers/SentRoutesContainer";
import {diffArrays} from "../../../Utils/DiffUtils";
import ProposedRoutesContainer from "../../../Redux/containers/ProposedRoutesContainer";
import SentRouteDialogContainer from "../../../Redux/containers/SentRouteDialogContainer";
import {evaluateOrderGroups} from "../../../Utils/OrdersGrouping";
import {sessionGet} from "../../../Utils/SessionStorageUtils";

export function injectOrdersToDeliveryOrders(deliveryOrders, orders) {
    return deliveryOrders.map(deliveryOrder => {
        return {...deliveryOrder, order: orders.find(order => order.id === deliveryOrder.order_id)}
    });
}

export function injectOrdersForGroups(groups, orders) {
    return groups.map((group) => {
        return {
            ...group,
            route: injectOrdersToDeliveryOrders(group.route, orders),
            aborted_orders: injectOrdersToDeliveryOrders(group.aborted_orders, orders)
        };
    });
}

export function injectGroupsToOrders(groups, orders, onlyOrdersInGroup = false) {
    const ordersGroupsMap = {};

    groups.forEach((group) => group.route.forEach(deliveryOrder => {
        ordersGroupsMap[deliveryOrder.order_id] = group.id;
    }));

    // Filter deliveryOrders only for those that is in the placeGroupsIndex
    const newOrdersWithGroups = orders.flatMap(order => {
        if (order.id in ordersGroupsMap)
            return [{...order, groupId: ordersGroupsMap[order.id]}];
        if (!onlyOrdersInGroup && ["READY", "ACCEPTED", "SCHEDULED_READY", "ASSIGNED_SCHEDULED_READY"].includes(order.status))
            return [order];

        else
            return []
    });
    return {groups, newOrdersWithGroups};
}

class DeliveryGroupsMap extends Component {
    constructor(props) {
        super(props);

        this.evaluateDeliveryGroupsTimeout = undefined;

        this.state = {
            proposedGroups: [],
            sentOrders: undefined,
            unsentOrders: undefined,
            assignedGroups: [],
            isSentRouteDialogOpen: false,
            selectedSentRouteForDialog: undefined,
            lockedGroupsIndex: {}
        };

        this.focusMessengerActiveGroupSubscription = eventsSubject
            .pipe(filter(f => f.topic === FocusMessengerActiveGroup))
            .subscribe(data => this.onFocusMessengerActiveGroup(data));
    }

    componentWillUnmount() {
        this.focusMessengerActiveGroupSubscription.unsubscribe();
    }

    getDataChanges(orders, business, users, proposedDeliveryGroups, assignedDeliveryGroups, lockedDeliveryGroups, sentLockedDeliveryGroups) {
        let isRequiredRouteEvaluation = false;
        const dataChanges = [];

        const sentOrderIds = (assignedDeliveryGroups || []).flatMap(deliveryGroup => deliveryGroup.route.map(deliveryOrder => deliveryOrder.order_id));
        const sentOrderIdsIndex = sentOrderIds.reduce((accumulator, currentValue) => {
            accumulator[currentValue] = true;
            return accumulator;
        }, {});

        const mappedOrders = orders.map(o => (
            {
                id: o.id,
                status: o.status,
                address: o.address,
                location: o.location,
                orderTime: o.orderTime
            }
        ));

        const sentOrders = mappedOrders.filter(order => order.id in sentOrderIdsIndex);
        const notSentOrders = mappedOrders.filter(order => !(order.id in sentOrderIdsIndex));
        const acceptedOrders = notSentOrders.filter(order => order.status === "ACCEPTED");
        const readyOrders = notSentOrders.filter(order => order.status === "READY");
        const scheduledReadyOrders = notSentOrders.filter(order => order.status === "SCHEDULED_READY");

        const clone = createClone();

        if (!_.isEqual(sentOrders, this.lastSentOrders)) {
            dataChanges.push('SENT_ORDERS');
            this.lastSentOrders = clone(sentOrders);
        }

        if (!_.isEqual(acceptedOrders, this.lastAcceptedOrders)) {
            dataChanges.push('ACCEPTED_ORDERS');
            this.lastAcceptedOrders = clone(acceptedOrders);
        }

        if (!_.isEqual(scheduledReadyOrders, this.lastScheduledReadyOrders)) {
            dataChanges.push('SCHEDULED_READY_ORDERS');
            this.lastScheduledReadyOrders = clone(scheduledReadyOrders);
        }

        if (!_.isEqual(readyOrders, this.lastReadyOrders)) {
            dataChanges.push('READY_ORDERS');
            this.lastReadyOrders = clone(readyOrders);
            isRequiredRouteEvaluation = true;
        }

        if (!_.isEqual(business, this.lastBusiness)) {
            dataChanges.push('BUSINESS');
            this.lastBusiness = clone(business);
            isRequiredRouteEvaluation = true;
        }

        if (!_.isEqualWith(users, this.lastUsers, (value1, value2, key) => {
            return ['location', 'lastNonAvailableTimestamp', 'lastAvailableTimestamp'].includes(key) ? true : undefined;
        })) {
            dataChanges.push('USERS');
            this.lastUsers = clone(users);
            isRequiredRouteEvaluation = true;
        }

        if (!_.isEqual(lockedDeliveryGroups, this.lastLockedDeliveryGroups)) {
            if (this.lastLockedDeliveryGroups && this.lastProposedDeliveryGroups) {
                const {
                    isChanged,
                    isRequiredRouteEvaluation: diffRequiresRouteEval
                } = this.diffLockedDeliveryGroups(this.lastLockedDeliveryGroups, lockedDeliveryGroups, sentLockedDeliveryGroups, this.lastProposedDeliveryGroups, proposedDeliveryGroups);

                if (isChanged)
                    dataChanges.push('LOCKED_DELIVERY_GROUPS');

                if (diffRequiresRouteEval)
                    isRequiredRouteEvaluation = true;
            } else
                dataChanges.push('LOCKED_DELIVERY_GROUPS');

            this.lastLockedDeliveryGroups = clone(lockedDeliveryGroups);
        }

        if (!_.isEqual(proposedDeliveryGroups, this.lastProposedDeliveryGroups)) {
            dataChanges.push('PROPOSED_DELIVERY_GROUPS');
            this.lastProposedDeliveryGroups = clone(proposedDeliveryGroups);
        }

        if (!_.isEqual(assignedDeliveryGroups, this.lastAssignedDeliveryGroups)) {
            dataChanges.push('ASSIGNED_DELIVERY_GROUPS');
            this.lastAssignedDeliveryGroups = clone(assignedDeliveryGroups);
        }

        return {dataChanges, isRequiredRouteEvaluation};
    }

    diffLockedDeliveryGroups(lastLockedDeliveryGroups, lockedDeliveryGroups, sentLockedDeliveryGroups, lastProposedDeliveryGroups, proposedDeliveryGroups) {
        const diffs = diffArrays(this.lastLockedDeliveryGroups, lockedDeliveryGroups, (obj) => obj.group_id);

        const ignoreChanges = ['timestamp'];

        let isChanged = false;
        let isRequiredRouteEvaluation = false;

        for (let diff of diffs) {
            if (diff.type === "add") {
                isChanged = true;

                const lockedGroup = lockedDeliveryGroups.find(g => g.group_id === diff.id);
                const proposedGroup = proposedDeliveryGroups.find(g => g.id === diff.id);

                if (!proposedGroup)
                    isRequiredRouteEvaluation = true;
                // If the order ids are not equal, the locked group's order ids have changed
                else if (!_.isEqual(
                    [...proposedGroup.route.map(stop => stop.order_id)].sort(),
                    [...lockedGroup.order_ids].sort()
                ))
                    isRequiredRouteEvaluation = true;
                else if (lockedGroup.is_ordered &&
                    !_.isEqual(
                        [...proposedGroup.route.map(stop => stop.order_id)],
                        [...lockedGroup.order_ids]
                    ))
                    isRequiredRouteEvaluation = true;
                else if (!isRequiredRouteEvaluation && lockedGroup.messenger_id) {
                    const lastProposedGroup = lastProposedDeliveryGroups.find(g => g.id === diff.id);
                    if ((lastProposedGroup ? lastProposedGroup.messenger_id : undefined) !== lockedGroup.messenger_id ||
                        (proposedGroup ? proposedGroup.messenger_id : undefined) !== lockedGroup.messenger_id)
                        isRequiredRouteEvaluation = true;
                }
            } else if (diff.type === "update") {
                const filteredDiffChanges = diff.changes.map(c => {
                    return {...c, path: c.path.join('.')}
                }).filter(change => !ignoreChanges.includes(change.path));

                if (filteredDiffChanges.length > 0) {
                    isChanged = true;

                    for (let change of filteredDiffChanges) {
                        if (change.path === "messenger_id") {
                            const lastProposedGroup = lastProposedDeliveryGroups.find(g => g.id === diff.id);

                            if ((change.val && change.val !== (lastProposedGroup ? lastProposedGroup.messenger_id : undefined)) || !change.val)
                                isRequiredRouteEvaluation = true;
                        } else if (change.path.startsWith("order_ids"))
                            isRequiredRouteEvaluation = true;
                    }
                }
            } else if (diff.type === "remove") {
                const sentLockedGroup = sentLockedDeliveryGroups.find(g => g.group_id === diff.id);

                if (!sentLockedGroup) {
                    isChanged = true;
                    isRequiredRouteEvaluation = true;
                }
            }
        }

        return {isChanged, isRequiredRouteEvaluation};
    }

    static isDataFetchingOrChanging(orders, business, users, deliveryGroups) {
        return orders.fetching || orders.changing ||
            business.fetching || business.changing ||
            users.fetching || users.changing ||
            deliveryGroups.fetching || deliveryGroups.changing
    }

    componentDidMount() {
        this.receiveData(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.receiveData(nextProps);
    }

    receiveData(props) {
        // Orders, business and users are not fetching,
        if (!DeliveryGroupsMap.isDataFetchingOrChanging(props.orders,
            props.business,
            props.users,
            props.deliveryGroups)) {

            const {dataChanges, isRequiredRouteEvaluation} = this.getDataChanges(
                props.orders.deliveryOrdersWithLocation,
                props.business.business,
                props.users.users,
                props.deliveryGroups.proposedDeliveryGroups,
                props.deliveryGroups.assignedDeliveryGroups,
                props.deliveryGroups.lockedDeliveryGroups,
                props.deliveryGroups.sentLockedDeliveryGroups
            );

            if (dataChanges.some(dataChange => ['SENT_ORDERS', 'ACCEPTED_ORDERS', 'READY_ORDERS', 'PROPOSED_DELIVERY_GROUPS', 'ASSIGNED_DELIVERY_GROUPS', 'LOCKED_DELIVERY_GROUPS'].includes(dataChange))) {
                const {proposedDeliveryGroups, assignedDeliveryGroups} = props.deliveryGroups;

                const assignedGroups = assignedDeliveryGroups ? injectOrdersForGroups(assignedDeliveryGroups, props.orders.deliveryOrdersWithLocation) : undefined;
                const assignedGroupsForOrders = assignedGroups ? injectGroupsToOrders(assignedGroups, props.orders.deliveryOrdersWithLocation, true) : undefined;

                const proposedGroups = proposedDeliveryGroups ? injectOrdersForGroups(proposedDeliveryGroups, props.orders.deliveryOrdersWithLocation) : undefined;
                const proposedGroupsForOrders = proposedGroups ? injectGroupsToOrders(proposedGroups, props.orders.deliveryOrdersWithLocation) : undefined;

                const newSelectedGroup = this.getUpdatedSelectedGroup(
                    proposedGroupsForOrders ? proposedGroupsForOrders.groups : [],
                    assignedGroupsForOrders ? assignedGroupsForOrders.groups : []);

                const newState = {
                    selectedSentRouteForDialog: newSelectedGroup,
                    business: this.props.business.business
                };

                if (dataChanges.includes("PROPOSED_DELIVERY_GROUPS"))
                    newState.proposedGroups = proposedGroupsForOrders ? proposedGroupsForOrders.groups : undefined;

                if (dataChanges.includes("ASSIGNED_DELIVERY_GROUPS"))
                    newState.assignedGroups = assignedGroupsForOrders ? assignedGroupsForOrders.groups : undefined;

                if (dataChanges.includes("LOCKED_DELIVERY_GROUPS"))
                    newState.lockedGroupsIndex = props.deliveryGroups.lockedDeliveryGroups
                        .reduce((obj, item) => ({...obj, [item.group_id]: item}), {});

                const orders_assigned_to_orca = props.orders.deliveryOrdersWithLocation.filter(o => o.status === "ASSIGNED_TO_ORCA");

                newState.sentOrders = assignedGroupsForOrders ? assignedGroupsForOrders.newOrdersWithGroups.concat(orders_assigned_to_orca) : undefined;

                newState.unsentOrders = proposedGroupsForOrders ?
                    proposedGroupsForOrders.newOrdersWithGroups :
                    props.orders.deliveryOrdersWithLocation.filter(order => ["ACCEPTED", "READY", "SCHEDULED_READY"].includes(order.status));

                this.setState(newState);
            }

            // Orders, users or business have been changed
            if (isRequiredRouteEvaluation) {
                this.onDataChanged(props.orders.deliveryOrdersWithLocation,
                    props.users.users.filter(user => _.get(user, "profiles.messenger.isCurrentlyAvailable")));
            }
        }
    }

    getUpdatedSelectedGroup(proposedGroups, assignedGroups) {
        if (!this.state.selectedSentRouteForDialog) return null;

        const groupId = this.state.selectedSentRouteForDialog.id;
        return [...proposedGroups, ...assignedGroups].find(group => group.id === groupId);
    }

    evaluateDeliveryGroups() {
        let grouping = undefined;

        if (this.props.isMerukazotEnabled) {
            const ignoredOrderIdsForGrouping = sessionGet("ignoredOrderIdsForGrouping") || [];
            const groups = evaluateOrderGroups(
                this.props.orders.deliveryOrders.filter(order => order.status === "READY"),
                this.props.merukazotMin,
                ignoredOrderIdsForGrouping);
            const lockedOrderIds = !this.props.deliveryGroups.lockedDeliveryGroups ? [] : this.props.deliveryGroups.lockedDeliveryGroups.flatMap(lockedGroup => lockedGroup.order_ids);
            const nonSingleGroups = groups.filter(group => group.length > 1 && !group.some(order => lockedOrderIds.includes(order.id)));

            grouping = {
                'groups': nonSingleGroups.map(groupOrders => ({
                    'groupId': "merukazot~" + uuid.v4(),
                    'orderIds': groupOrders.map(order => order.id),
                    'isLocked': true,
                    'isOrdered': true
                }))
            }
        }

        this.props.evaluateDeliveryGroups(this.props.match.params.bid, grouping);
    }

    onDataChanged(orders, messengers) {
        this.setState({
            isNoAvailableMessengers: messengers.length === 0
        });

        if (this.evaluateDeliveryGroupsTimeout)
            clearTimeout(this.evaluateDeliveryGroupsTimeout);

        this.evaluateDeliveryGroupsTimeout = setTimeout(() => {
            this.evaluateDeliveryGroups();
            this.evaluateDeliveryGroupsTimeout = undefined;
        }, 50);
    }

    onStopDoubleClick(stopId) {
        const order = this.state.unsentOrders.find(order => order.id === stopId);

        if (order && order.status === 'READY' && order.groupId !== undefined) {
            this.setState({
                orderForGroupChangeDialog: order
            });
        }
    }

    async onRemoveStopFromRouteClick(routeId, stopId) {
        const route = this.getProposedRoutesIndex()[routeId];

        await this.removeStopFromRoute(route, stopId);
    }

    onFocusMessengerActiveGroup(m) {
        if (!m.data.groupId) {
            this.setState({
                isSentRouteDialogOpen: false,
                selectedSentRouteForDialog: undefined
            });

            return;
        }

        if (this.state.isSentRouteDialogOpen) {
            this.setState({
                isSentRouteDialogOpen: false,
                selectedSentRouteForDialog: undefined
            });
        }

        setTimeout(() => {
            this.setState({
                isSentRouteDialogOpen: true,
                selectedSentRouteForDialog: this.state.assignedGroups.find(group => group.id === m.data.groupId)
            });
        }, isDialogOpen ? 400 : 0);
    }

    onSentRouteClicked(e, groupId) {
        this.setState({
            isSentRouteDialogOpen: true,
            selectedSentRouteForDialog: this.state.assignedGroups.find(group => group.id === groupId)
        });
    }

    onSentRouteDialogClosed() {
        this.setState({
            isSentRouteDialogOpen: false,
            selectedSentRouteForDialog: null
        });
    }

    handleOrderMapDoubleClick(orderId) {
        /* TODO: Change order group dialog */
        const order = this.state.unsentOrders.find(order => order.id === orderId);

        if (order && order.status === 'READY' && order.groupId !== undefined) {
            this.setState({
                orderForGroupChangeDialog: order
            });
        }
    }

    handleChangeOrderGroupCancel() {
        this.setState({
            orderForGroupChangeDialog: undefined
        });
    }

    async handleChangeOrderGroupSave(order, newRouteId) {
        this.setState({
            orderForGroupChangeDialog: undefined
        });

        if (this.props.orders.deliveryOrdersWithLocation.length === 0) {
            this.setState({
                ordersForMap: [],
                groups: []
            });

            return;
        }

        if (this.props.orders.fetching || this.props.orders.changing ||
            this.props.business.fetching || this.props.business.changing)
            return;

        const routesIndex = this.getProposedRoutesIndex();
        const fromRoute = routesIndex[order.groupId];
        const toRoute = newRouteId in routesIndex ? routesIndex[newRouteId] : {id: newRouteId, orderIds: []};

        await this.moveStopToRoute(fromRoute, toRoute, order.id);
    }

    async moveStopToRoute(fromGroup, toGroup, orderId) {
        await this.removeStopFromRoute(fromGroup, orderId);
        await this.addStopToRoute(toGroup, orderId);
    }

    async onMoveOrderToRoute(orderId, sourceRouteId, destinationRouteId) {
        const routesIndex = this.getProposedRoutesIndex();
        const sourceRoute = routesIndex[sourceRouteId];
        const destinationRoute = destinationRouteId === "new" ? {
            id: uuid.v4(),
            orderIds: []
        } : routesIndex[destinationRouteId];

        await this.moveStopToRoute(sourceRoute, destinationRoute, orderId);
    }

    async addStopToRoute(route, orderId) {
        if (route.isLocked) {
            await this.props.addOrdersToLockedGroup({
                bid: this.props.match.params.bid,
                groupId: route.id,
                orderIds: [orderId]
            });
        } else {
            await this.props.lockDeliveryGroup({
                bid: this.props.match.params.bid,
                groupId: route.id,
                orderIds: route.orderIds.concat([orderId]),
                isRouteOrdersChanged: true
            });
        }
    }

    async removeStopFromRoute(group, orderId) {
        if (group.isLocked) {
            await this.props.removeOrdersFromLockedGroup({
                bid: this.props.match.params.bid,
                groupId: group.id,
                orderIds: [orderId]
            });
        } else if (group.orderIds.length !== 1) {
            await this.props.lockDeliveryGroup({
                bid: this.props.match.params.bid,
                groupId: group.id,
                orderIds: group.orderIds.filter(groupOrderId => groupOrderId !== orderId),
                isRouteOrdersChanged: true
            });
        }
    }


    getProposedRoutesIndex() {
        return this.state.proposedGroups.reduce((groupOrdersMap, group) => {
            groupOrdersMap[group.id] = {
                id: group.id,
                isLocked: (group.id in this.state.lockedGroupsIndex) || false,
                orderIds: group.route.map(deliveryOrder => deliveryOrder.order_id)
            };
            return groupOrdersMap;
        }, {});
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !shallowEqualObjects({
            match: this.props.match,
            isNoAvailableMessengers: this.state.isNoAvailableMessengers,
            evaluatingProposedDeliveryGroups: this.props.deliveryGroups.evaluatingProposedDeliveryGroups,
            business: this.props.business.business,
            sentOrders: this.state.sentOrders,
            unsentOrders: this.state.unsentOrders,
            users: this.props.users.users,
            selectedSentRouteForDialog: this.state.selectedSentRouteForDialog,
            isSentRouteDialogOpen: this.state.isSentRouteDialogOpen,
            orderForGroupChangeDialog: this.state.orderForGroupChangeDialog,
            lockedGroupsIndex: this.state.lockedGroupsIndex
        }, {
            match: nextProps.match,
            isNoAvailableMessengers: nextState.isNoAvailableMessengers,
            evaluatingProposedDeliveryGroups: nextProps.deliveryGroups.evaluatingProposedDeliveryGroups,
            business: nextProps.business.business,
            sentOrders: nextState.sentOrders,
            unsentOrders: nextState.unsentOrders,
            users: nextProps.users.users,
            selectedSentRouteForDialog: nextState.selectedSentRouteForDialog,
            isSentRouteDialogOpen: nextState.isSentRouteDialogOpen,
            orderForGroupChangeDialog: nextState.orderForGroupChangeDialog,
            lockedGroupsIndex: nextState.lockedGroupsIndex
        });
    }

    render() {
        const {proposedGroups, assignedGroups, lockedGroupsIndex} = this.state;

        return (
            <div className="map-groupstoolbar-wrapper">
                <div className="delivery-map-wrapper">
                    <DeliveryMapContainer match={this.props.match}
                                          onMarkerDoubleClick={this.handleOrderMapDoubleClick.bind(this)}
                                          isNoAvailableMessengers={this.state.isNoAvailableMessengers}
                                          isLoading={this.props.deliveryGroups.evaluatingProposedDeliveryGroups}
                                          business={this.props.business.business}
                                          sentOrders={this.state.sentOrders}
                                          unsentOrders={this.state.unsentOrders}
                                          users={this.props.users.users}
                    />
                    <div className="assigned-groups-toolbar-wrapper">
                        <SentRoutesContainer onGroupClick={this.onSentRouteClicked.bind(this)}
                                             routes={assignedGroups}/>
                    </div>
                </div>
                <div
                    className={`proposed-groups-toolbar-wrapper ` + (proposedGroups && proposedGroups.length ? 'groups-toolbar-wrapper-show' : 'groups-toolbar-wrapper-hide')}>
                    <ProposedRoutesContainer onStopDoubleClick={this.onStopDoubleClick.bind(this)}
                                             onRemoveStopFromRouteClick={this.onRemoveStopFromRouteClick.bind(this)}
                                             onMoveOrderToRoute={this.onMoveOrderToRoute.bind(this)}
                                             routes={proposedGroups}
                                             lockedRoutesIndex={lockedGroupsIndex}
                    />
                </div>
                {
                    this.state.isSentRouteDialogOpen ?
                        <SentRouteDialogContainer route={this.state.selectedSentRouteForDialog}
                                                  onClose={this.onSentRouteDialogClosed.bind(this)}
                                                  isOpen={this.state.isSentRouteDialogOpen}/>
                        :
                        null
                }
                <ChangeGroupDialog open={this.state.orderForGroupChangeDialog !== undefined}
                                   proposedGroups={proposedGroups}
                                   assignedGroups={assignedGroups}
                                   order={this.state.orderForGroupChangeDialog}
                                   onSave={this.handleChangeOrderGroupSave.bind(this)}
                                   onCancel={this.handleChangeOrderGroupCancel.bind(this)}/>
            </div>
        );
    }
}

export default DeliveryGroupsMap;